import { Colors } from '../../ui/colors'
import type { StationExit } from '../../services/api'

// Exit classification + de-duplication for the platform diagram. A plain module so both the
// diagram and the sheet's accessibility badge can read it.

/**
 * The diagram is a technical drawing with its own internal contrast, so it carries a named
 * palette rather than reaching for `Colors.*` inline.
 *
 * The train is the LIGHT object on the card — a steel gradient with near-black door
 * openings — because on a black panel a dark car on a dark field is invisible, and the card
 * reads as a lit platform seen from above. `field` matches the card it sits in, so the
 * drawing has no visible frame of its own.
 */
export const DIAGRAM = {
  field: Colors.surface,
  /** Car body: the steel gradient's midpoint, for callers that need one flat value. */
  body: '#b6bdc8',
  bodyGradient: 'linear-gradient(180deg,#cfd4dd,#98a0ad)',
  bodyStroke: 'rgba(255,255,255,0.35)',
  /** Door openings punched out of the car body — near-black, the mockup's own value. */
  window: '#10131a',
  carDivider: Colors.surface,
  dropLine: 'rgba(255,255,255,0.25)',
  dotFill: '#ffffff',
  stem: 'rgba(255,255,255,0.25)',
  label: Colors.textSecondary,
} as const

export function exitTypeIcon(name: string): string {
  const n = name.toLowerCase()
  if (n.includes('elevator')) return '🛗'
  if (n.includes('escalator')) return '🪜'
  return '↑'
}

export function exitTypeColor(name: string): string {
  const n = name.toLowerCase()
  if (n.includes('elevator')) return Colors.primary
  if (n.includes('escalator')) return Colors.success
  return Colors.text
}

export function hasElevator(exits: StationExit[]): boolean {
  return exits.some((e) => e.name.toLowerCase().includes('elevator'))
}

export function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

// 65 m ÷ 111 320 m/° ≈ 0.000584° latitude
const DEDUP_LAT_THRESHOLD = 0.00006

export function dedupeExits(exits: StationExit[]): StationExit[] {
  // Exits with street data first: the first kept entry per group becomes the representative
  // label in the diagram and legend.
  const sorted = [...exits].sort((a, b) => {
    if (a.street && !b.street) return -1
    if (!a.street && b.street) return 1
    return 0
  })
  const kept: StationExit[] = []
  for (const exit of sorted) {
    // Two exits of the same type within 65 m are one physical entrance.
    const isDupe = kept.some(
      (k) =>
        exitTypeIcon(k.name) === exitTypeIcon(exit.name) &&
        Math.abs(k.lat - exit.lat) < DEDUP_LAT_THRESHOLD,
    )
    if (!isDupe) kept.push(exit)
  }
  return kept
}
